import { postPipeSeries } from "@imi/api-client/index.js";
import {
  CustomPipeSeries,
  customPipeSeriesSchema,
} from "@src/core/api/customPipeSeries";
import { extractDefaultValues } from "@src/core/engine/helpers/extractDefaultValues";
import { pipesRepository } from "@src/core/engine/pipes/pipesRepository";
import { useToast } from "@src/shared/components/Toast/useToast";
import { useAppForm } from "@src/shared/helpers/form";
import { logger } from "@src/shared/helpers/logger";
import { parseApiError } from "@src/shared/helpers/parseApiError";
import { revalidateLogic } from "@tanstack/react-form";
import { tryit } from "radashi";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type UseCustomPipeSeriesFormOptions = {
  onSubmit?: (submission: CustomPipeSeries) => void;
};

const definitions = pipesRepository.getAddDefinitions();
export function useCustomPipeSeriesForm(
  options?: UseCustomPipeSeriesFormOptions,
) {
  const { addToast } = useToast();
  const { t } = useTranslation("settings", { keyPrefix: "pipeSeries.add" });
  const [submitting, setSubmitting] = useState(false);

  const defaultValues = useMemo(() => {
    const defaultValues = extractDefaultValues(definitions);
    return {
      ...defaultValues,
      pipes: [defaultValues.pipes],
    };
  }, []);

  const form = useAppForm({
    defaultValues,
    async onSubmit({ value }) {
      setSubmitting(true);
      const [error, data] = await tryit(postPipeSeries)({ body: value });
      setSubmitting(false);

      if (error) {
        const apiError = parseApiError({ error });
        logger.error("Failed to create custom pipe series", {
          apiError,
          error,
        });
        return;
      }
      const pipeSeries = data.data;

      addToast({
        duration: 3000,
        message: t("success.description"),
        title: t("success.title"),
      });
      options?.onSubmit?.({
        ...pipeSeries,
        id: pipeSeries.id.toString(),
        pipes: pipeSeries.pipes.map((pipe) => ({
          ...pipe,
          id: pipe.id.toString(),
        })),
      });
    },
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: { onDynamic: customPipeSeriesSchema },
  });

  return {
    defaultValues,
    definitions,
    form,
    submitting,
  };
}
